import type { AgentGroup, GroupInput } from "@codvero/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { projectsApi } from "@/api/projects.ts";
import { studioApi } from "@/api/studio.ts";
import { Alert, Button, Field, Input, PageTitle, Select } from "@/ui/primitives.tsx";
import { useApiError } from "@/ui/useApiError.ts";
import { useConfirm } from "@/ui/ConfirmDialog.tsx";

const blank: GroupInput = { handle: "", name: "", description: "", leadAgentId: null, memberIds: [], projectIds: [] };

export function GroupsPage() {
  const { t } = useTranslation();
  const toMsg = useApiError();
  const qc = useQueryClient();
  const groups = useQuery({ queryKey: ["studio", "groups"], queryFn: studioApi.groups });
  const agents = useQuery({ queryKey: ["studio", "agents"], queryFn: studioApi.agents });
  const projects = useQuery({ queryKey: ["projects"], queryFn: projectsApi.list });
  const [editing, setEditing] = useState<{ id: string | null; form: GroupInput } | null>(null);
  const refresh = () => qc.invalidateQueries({ queryKey: ["studio", "groups"] });
  const save = useMutation({ mutationFn: ({ id, form }: { id: string | null; form: GroupInput }) => (id ? studioApi.updateGroup(id, form) : studioApi.createGroup(form)), onSuccess: () => { setEditing(null); void refresh(); } });
  const remove = useMutation({ mutationFn: studioApi.removeGroup, onSuccess: refresh });
  const { confirm } = useConfirm();
  const askRemove = async (g: { id: string; name: string }) => { if (await confirm({ title: t("confirm.deleteGroupTitle"), message: t("confirm.deleteGroupMsg", { name: g.name }), confirmLabel: t("common.delete"), danger: true })) remove.mutate(g.id); };
  const byId = new Map((agents.data ?? []).map((a) => [a.id, a]));
  const f = editing?.form;

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <PageTitle title={t("studio.groups.title")} subtitle={t("studio.groups.subtitle")} />
        <Button size="sm" onClick={() => setEditing({ id: null, form: { ...blank } })}><Plus className="size-4" /> {t("studio.groups.new")}</Button>
      </div>
      {(save.error || remove.error) && <Alert tone="danger">{toMsg(save.error ?? remove.error)}</Alert>}
      {f && (
        <form className="grid gap-4 rounded-lg border border-border bg-surface p-5 sm:grid-cols-2" onSubmit={(e) => { e.preventDefault(); save.mutate({ id: editing!.id, form: f }); }}>
          <Field label={t("studio.fields.name")}><Input required value={f.name} onChange={(e) => setEditing({ ...editing!, form: { ...f, name: e.target.value } })} /></Field>
          <Field label={t("studio.fields.handle")} hint={t("studio.fields.handleHint")}><Input dir="ltr" required className="font-mono" value={f.handle} onChange={(e) => setEditing({ ...editing!, form: { ...f, handle: e.target.value.toLowerCase() } })} /></Field>
          <div className="sm:col-span-2"><Field label={t("studio.fields.description")}><Input value={f.description} onChange={(e) => setEditing({ ...editing!, form: { ...f, description: e.target.value } })} /></Field></div>
          <div>
            <div className="mb-1.5 text-sm font-medium">{t("studio.groups.members")}</div>
            <div className="max-h-48 space-y-1 overflow-auto rounded-md border border-border p-2 text-sm">
              {(agents.data ?? []).filter((a) => a.status !== "ARCHIVED").map((a) => (
                <label key={a.id} className="flex items-center gap-2"><input type="checkbox" checked={f.memberIds.includes(a.id)} onChange={(e) => setEditing({ ...editing!, form: { ...f, memberIds: e.target.checked ? [...f.memberIds, a.id] : f.memberIds.filter((x) => x !== a.id), leadAgentId: !e.target.checked && f.leadAgentId === a.id ? null : f.leadAgentId } })} /> {a.avatar.emoji} {a.name} <span dir="ltr" className="text-xs text-fg-muted">@{a.handle}</span></label>
              ))}
            </div>
          </div>
          <div className="space-y-3">
            <Field label={t("studio.groups.lead")} hint={t("studio.groups.leadHint")}>
              <Select value={f.leadAgentId ?? ""} onChange={(e) => setEditing({ ...editing!, form: { ...f, leadAgentId: e.target.value || null } })}>
                <option value="">—</option>
                {f.memberIds.map((id) => <option key={id} value={id}>{byId.get(id)?.name ?? id}</option>)}
              </Select>
            </Field>
            <div>
              <div className="mb-1.5 text-sm font-medium">{t("studio.fields.projects")}</div>
              <div className="max-h-32 space-y-1 overflow-auto rounded-md border border-border p-2 text-sm">
                {(projects.data ?? []).map((p) => <label key={p.id} className="flex items-center gap-2"><input type="checkbox" checked={f.projectIds.includes(p.id)} onChange={(e) => setEditing({ ...editing!, form: { ...f, projectIds: e.target.checked ? [...f.projectIds, p.id] : f.projectIds.filter((x) => x !== p.id) } })} /> {p.name}</label>)}
              </div>
            </div>
          </div>
          <div className="flex gap-2 sm:col-span-2">
            <Button type="submit" loading={save.isPending}>{t("common.save")}</Button>
            <Button type="button" variant="ghost" onClick={() => setEditing(null)}>{t("common.cancel")}</Button>
          </div>
        </form>
      )}
      {groups.data?.length === 0 && !editing && <div className="rounded-lg border border-dashed border-border p-10 text-center text-sm text-fg-muted">{t("studio.groups.empty")}</div>}
      <div className="grid gap-3 md:grid-cols-2">
        {(groups.data ?? []).map((g: AgentGroup) => (
          <div key={g.id} className="rounded-lg border border-border bg-surface p-4">
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="font-semibold">{g.name} <span dir="ltr" className="font-mono text-xs text-fg-muted">@{g.handle}</span></div>
                {g.description && <p className="text-xs text-fg-muted">{g.description}</p>}
              </div>
              <div className="flex gap-1">
                <button type="button" className="rounded p-1 text-fg-muted hover:text-fg" onClick={() => setEditing({ id: g.id, form: { handle: g.handle, name: g.name, description: g.description, leadAgentId: g.leadAgentId, memberIds: g.memberIds, projectIds: g.projectIds } })}><Pencil className="size-4" /></button>
                <button type="button" className="rounded p-1 text-fg-muted hover:text-danger" onClick={() => void askRemove(g)}><Trash2 className="size-4" /></button>
              </div>
            </div>
            <div className="mt-3 flex flex-wrap gap-1">
              {g.memberIds.map((id) => { const a = byId.get(id); return a ? <span key={id} className="flex items-center gap-1 rounded-full border border-border px-2 py-0.5 text-xs">{a.avatar.emoji} {a.name}{g.leadAgentId === id && <span className="rounded bg-accent-soft px-1 text-[10px]">{t("studio.groups.leadBadge")}</span>}</span> : null; })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
